/* Copyright (c) 2020-present CloudIO Inc */

import {
  AnalyzeContext,
  AnalyzeIssue,
  AnalyzeResult,
} from 'layout/Sidebar/panels/TreeView/AnalyzeUtils';
import { itemWithDefaultValues } from 'registry/Components';
import { Item, RequiredItem } from '../../registry/Component.type';
import { pageAtom } from 'designer/atoms';

export async function analyzeTransaction({
  platform,
  appUid,
  pageId,
  itemId,
  mgr,
}: AnalyzeContext): Promise<AnalyzeResult> {
  const alerts: AnalyzeIssue[] = [];
  const autoFixes: (() => void)[] = [];
  const page = platform.getPage(appUid, pageId);
  if (!page) {
    return null;
  }
  const item = itemWithDefaultValues(
    page[itemId],
  ) as RequiredItem<'Transaction'>;
  const { alias } = item.props;
  const parentTxn = page[item.parent];
  if (parentTxn.type === 'Transaction') {
    const itemIds = Object.keys(page);
    const dsItem = itemIds
      .filter(
        (id) =>
          page[id]?.type === 'DataStore' ||
          page[id]?.type === 'ReadOnlyDataStore' ||
          page[id]?.type === 'LocalDataStore' ||
          page[id]?.type === 'TransientDataStore',
      )
      .map((id) => page[id] as Item<'ReadOnlyDataStore'>)
      .find((ds) => ds.props.alias === alias);

    if (!dsItem) {
      alerts.push({
        key: `missing-alias-${itemId}`,
        showItemId: itemId,
        alert: `Missing data store for alias ${alias}!`,
        itemId,
      });
      return [alerts, autoFixes];
    }

    if (dsItem.props.autoQuery) {
      const fix = () => {
        const _page = { ...platform.getPage(appUid, pageId) };
        const udpatedDs: Item<'ReadOnlyDataStore'> = {
          ...dsItem,
          props: { ...dsItem.props, autoQuery: false },
        };
        _page[dsItem.itemId] = udpatedDs;
        platform.setter(pageAtom, _page, {
          params: { appUid, pageId },
        });
      };
      autoFixes.push(fix);
      alerts.push({
        key: `auto-${itemId}`,
        fix,
        showItemId: dsItem.itemId,
        alert: `Data Store [${alias}] used by a child transaction has auto query on!`,
        tip: `Transaction component controls the data store query or it must be programmatically controlled, so turn off the auto query.

 > Click \`Fix It\` to turn off the auto query on data store with alias ${alias}.`,
        itemId,
      });
    }
  }

  return [alerts, autoFixes];
}
